import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDownIcon,
  QuestionMarkCircleIcon,
  AppleIcon,
  AndroidIcon,
  DownloadIcon,
  UserPlusIcon,
  CheckCircleIcon,
  ExternalLinkIcon,
} from '../icons';

interface VCardInstructionsProps {
  accentColor: string;
}

const IOS_SUPPORT_URL = 'https://support.apple.com/guide/iphone/add-and-use-contact-information-iph3e0f4671/ios';
const ANDROID_SUPPORT_URL = 'https://support.google.com/contacts/answer/15147365?hl=en&co=GENIE.Platform%3DAndroid';

export default function VCardInstructions({ accentColor }: VCardInstructionsProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mt-4 bg-gray-50 rounded-xl overflow-hidden">
      {/* Collapsible header */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <QuestionMarkCircleIcon className="w-5 h-5 text-gray-500" />
          <span className="font-medium text-gray-700 text-sm">
            {t('profile.vcardInstructions.title')}
          </span>
        </div>
        <ChevronDownIcon
          className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Collapsible content */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 pb-4 space-y-4">
          {/* iOS Section */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: accentColor }}
              >
                <AppleIcon className="w-4 h-4 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900">
                {t('profile.vcardInstructions.ios.title')}
              </h4>
            </div>

            <div className="space-y-3">
              <Step
                number={1}
                icon={<DownloadIcon className="w-3.5 h-3.5 text-white" />}
                text={t('profile.vcardInstructions.ios.step1')}
                accentColor={accentColor}
              />
              <Step
                number={2}
                icon={<UserPlusIcon className="w-3.5 h-3.5 text-white" />}
                text={t('profile.vcardInstructions.ios.step2')}
                accentColor={accentColor}
              />
              <Step
                number={3}
                icon={<CheckCircleIcon className="w-3.5 h-3.5 text-white" />}
                text={t('profile.vcardInstructions.ios.step3')}
                accentColor={accentColor}
              />
            </div>

            <a
              href={IOS_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-mk-pink-600 hover:text-mk-pink-700 hover:underline transition-colors"
            >
              {t('profile.vcardInstructions.ios.learnMore')}
              <ExternalLinkIcon className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Android Section */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: accentColor }}
              >
                <AndroidIcon className="w-4 h-4 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900">
                {t('profile.vcardInstructions.android.title')}
              </h4>
            </div>

            <div className="space-y-3">
              <Step
                number={1}
                icon={<DownloadIcon className="w-3.5 h-3.5 text-white" />}
                text={t('profile.vcardInstructions.android.step1')}
                accentColor={accentColor}
              />
              <Step
                number={2}
                icon={<UserPlusIcon className="w-3.5 h-3.5 text-white" />}
                text={t('profile.vcardInstructions.android.step2')}
                accentColor={accentColor}
              />
              <Step
                number={3}
                icon={<CheckCircleIcon className="w-3.5 h-3.5 text-white" />}
                text={t('profile.vcardInstructions.android.step3')}
                accentColor={accentColor}
              />
            </div>

            <a
              href={ANDROID_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-mk-pink-600 hover:text-mk-pink-700 hover:underline transition-colors"
            >
              {t('profile.vcardInstructions.android.learnMore')}
              <ExternalLinkIcon className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

interface StepProps {
  number: number;
  icon: React.ReactNode;
  text: string;
  accentColor: string;
}

function Step({ icon, text, accentColor }: StepProps) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ backgroundColor: accentColor, opacity: 0.8 }}
      >
        {icon}
      </div>
      <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
    </div>
  );
}
